import { useState } from "react";
import { Lock, LogOut, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuthUser, useLogout } from "../lib/auth";
import { apiUrl } from "../lib/api";
import { getStoredTheme, setTheme, type Theme } from "../lib/theme";
import {
  useAddWhitelist,
  useRemoveWhitelist,
  useWhitelist,
  type WhitelistEntry,
} from "../lib/settings";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WhitelistSettingsTab() {
  const { data: meData } = useAuthUser();
  const myEmail = meData?.email.toLowerCase() ?? "";
  const { data, isLoading } = useWhitelist();
  const add = useAddWhitelist();
  const remove = useRemoveWhitelist();
  const [input, setInput] = useState("");
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);

  const items = data?.items ?? [];

  const submit = () => {
    const email = input.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast.error("Bitte eine gültige Email eingeben");
      return;
    }
    add.mutate(email, {
      onSuccess: () => {
        toast.success("Email hinzugefügt");
        setInput("");
      },
      onError: (err) => {
        toast.error(`Hinzufügen fehlgeschlagen: ${(err as Error).message}`);
      },
    });
  };

  const performRemove = (email: string) => {
    remove.mutate(email, {
      onSuccess: () => toast.success("Email entfernt"),
      onError: (err) =>
        toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`),
    });
    setConfirmTarget(null);
  };

  const tryRemove = (email: string) => {
    if (email === myEmail) {
      setConfirmTarget(email);
    } else {
      performRemove(email);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-text-secondary">
          Diese Emails dürfen sich am Admin-UI anmelden. Bootstrap-Admins (aus
          der <code>ADMIN_WHITELIST</code> Env-Variable) sind durch ein
          Schloss-Symbol markiert und können hier nicht entfernt werden.
        </p>
      </div>

      <div className="overflow-hidden rounded-md border border-border-primary">
        {isLoading ? (
          <div className="space-y-1.5 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-9 animate-pulse rounded-md bg-bg-secondary"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-text-tertiary">
            Keine Einträge.
          </div>
        ) : (
          <ul className="divide-y divide-border-secondary">
            {items.map((entry) => (
              <WhitelistRow
                key={entry.email}
                entry={entry}
                isMe={entry.email === myEmail}
                onRemove={() => tryRemove(entry.email)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="neue.email@team.de"
          className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={add.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
        >
          <Plus size={14} /> Hinzufügen
        </button>
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
        title="Eigene Email entfernen?"
        variant="danger"
        confirmLabel="Trotzdem entfernen"
        description={
          <>
            Du bist gerade als <strong>{confirmTarget}</strong> angemeldet.
            Wenn du dich entfernst, kannst du dich nach dem nächsten Logout
            <strong> nicht mehr einloggen</strong> — außer eine andere Person
            mit Zugang fügt dich wieder hinzu.
          </>
        }
        onConfirm={() => confirmTarget && performRemove(confirmTarget)}
      />
    </div>
  );
}

function WhitelistRow({
  entry,
  isMe,
  onRemove,
}: {
  entry: WhitelistEntry;
  isMe: boolean;
  onRemove: () => void;
}) {
  const isEnv = entry.source === "env";
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
      {isEnv ? (
        <span title="Aus ADMIN_WHITELIST Env-Variable" className="text-text-tertiary">
          <Lock size={14} />
        </span>
      ) : (
        <span className="inline-block h-3 w-3 rounded-full bg-success" />
      )}
      <span className="font-mono text-text-primary">{entry.email}</span>
      {isMe && (
        <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 font-mono text-xs text-accent-text">
          du
        </span>
      )}
      {entry.addedBy && (
        <span className="ml-2 text-xs text-text-tertiary">
          von {entry.addedBy}
          {entry.addedAt &&
            ` · ${new Date(entry.addedAt).toLocaleDateString("de-DE")}`}
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={isEnv}
        aria-label={`${entry.email} entfernen`}
        title={
          isEnv
            ? "Bootstrap-Admin (Env-Variable) — über CLI/Railway entfernen"
            : "Entfernen"
        }
        className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-danger-bg hover:text-danger disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-text-secondary"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

// ---- Profile ----

export function ProfileSettingsTab() {
  const { data: user } = useAuthUser();
  const logout = useLogout();

  if (!user) return null;

  const displayName = user.name || user.email;
  const initials = (user.name || user.email || "?")
    .split(/[\s@.]+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4 rounded-md border border-border-primary bg-bg-secondary p-5">
        <div className="inline-flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-border-primary bg-bg-primary text-lg font-medium text-text-primary">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div>
          <p className="text-base font-semibold text-text-primary">
            {displayName}
          </p>
          <p className="font-mono text-xs text-text-secondary">{user.email}</p>
        </div>
      </div>

      <div className="space-y-2">
        <a
          href={apiUrl("/auth/google")}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
        >
          Mit Google neu verbinden
        </a>
        <p className="text-xs text-text-tertiary">
          Bei Sitzungsproblemen oder nach Wechsel des Google-Kontos.
        </p>
      </div>

      <div>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-bg-primary px-3 py-1.5 text-sm font-medium text-danger hover:bg-danger-bg"
        >
          <LogOut size={14} /> Abmelden
        </button>
      </div>
    </div>
  );
}

// ---- Appearance ----

export function AppearanceSettingsTab() {
  const [current, setCurrent] = useState<Theme>(getStoredTheme());

  const apply = (next: Theme) => {
    setCurrent(next);
    setTheme(next);
  };

  const options: { v: Theme; label: string; help: string }[] = [
    { v: "system", label: "System", help: "Folgt der OS-Einstellung." },
    { v: "light", label: "Hell", help: "Immer helle Oberfläche." },
    { v: "dark", label: "Dunkel", help: "Immer dunkle Oberfläche." },
  ];

  return (
    <div className="space-y-5">
      <div>
        <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Theme
        </p>
        <p className="text-sm text-text-secondary">
          Wird im Browser gespeichert (localStorage). Wirkt sofort.
        </p>
      </div>

      <div className="space-y-2">
        {options.map((o) => (
          <label
            key={o.v}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
              current === o.v
                ? "border-accent bg-accent-subtle"
                : "border-border-primary bg-bg-secondary hover:bg-bg-hover"
            }`}
          >
            <input
              type="radio"
              name="theme"
              checked={current === o.v}
              onChange={() => apply(o.v)}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-text-primary">{o.label}</p>
              <p className="text-xs text-text-secondary">{o.help}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
